/**
 * Creative provider credentials, per organization.
 *
 * Provider keys used to live in process env, so every organization on a
 * deployment shared one Higgsfield account. This is where an agency brings
 * its own.
 *
 * Two rules the UI enforces because the API does:
 *   - A stored secret is never displayed. Saved fields show only that they are
 *     set and their last four characters.
 *   - Secret fields default to a Secret Manager *reference*. Storing the raw
 *     value is possible, and marked as the weaker option, because it lands in
 *     Firestore in plaintext.
 */
import { useCallback, useEffect, useState } from 'react';
import { parseHiggsfieldKey } from './higgsfieldKey';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  creativeProvidersApi,
  OrgProvider,
  ProviderCredentialSpecField,
} from '@/services/api';

interface Props {
  orgUid: string;
}

type TestState = { ok: boolean; detail: string; verified?: boolean } | undefined;

export function CreativeProvidersSection({ orgUid }: Props) {
  const [providers, setProviders] = useState<OrgProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [completeKey, setCompleteKey] = useState('');
  // Per secret field: store a Secret Manager reference (default) or a literal.
  const [useRef, setUseRef] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, TestState>>({});

  const load = useCallback(async () => {
    if (!orgUid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await creativeProvidersApi.listForOrg(orgUid);
      setProviders(data.providers ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, [orgUid]);

  useEffect(() => {
    void load();
  }, [load]);

  const beginEdit = (provider: OrgProvider) => {
    setEditing(provider.provider_id);
    setDraft({});
    setCompleteKey('');
    setError(null);
    // Secret fields default to a reference; that is the form we want people
    // reaching for without having to think about it.
    setUseRef(
      Object.fromEntries(
        provider.credential_spec.filter((f) => f.secret).map((f) => [f.key, true])
      )
    );
  };

  const save = async (provider: OrgProvider) => {
    setSaving(true);
    setError(null);
    try {
      const values: Record<string, string> = {};
      for (const [key, value] of Object.entries(draft)) {
        if (!value.trim()) continue;
        const spec = provider.credential_spec.find((f) => f.key === key);
        const asRef = spec?.secret && useRef[key];
        values[asRef ? `${key}_ref` : key] = value.trim();
      }
      if (provider.provider_id === 'higgsfield' && completeKey.trim()) {
        Object.assign(values, parseHiggsfieldKey(completeKey));
        delete values.key_id_ref;
        delete values.key_secret_ref;
      }
      if (Object.keys(values).length === 0) {
        throw new Error('Enter a credential to save. Use Remove org key to clear saved credentials.');
      }
      await creativeProvidersApi.setCredentials(orgUid, provider.provider_id, values);
      setEditing(null);
      setDraft({});
      setCompleteKey('');
      setTests((t) => ({ ...t, [provider.provider_id]: undefined }));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const clear = async (providerId: string) => {
    setSaving(true);
    try {
      await creativeProvidersApi.clearCredentials(orgUid, providerId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to clear credentials');
    } finally {
      setSaving(false);
    }
  };

  const test = async (providerId: string) => {
    setTesting(providerId);
    try {
      const result = await creativeProvidersApi.test(orgUid, providerId);
      setTests((t) => ({
        ...t,
        [providerId]: {
          ok: result.ok,
          detail: result.detail || result.reason || '',
          verified: result.verified,
        },
      }));
    } catch (e) {
      setTests((t) => ({
        ...t,
        [providerId]: { ok: false, detail: e instanceof Error ? e.message : 'failed' },
      }));
    } finally {
      setTesting(null);
    }
  };

  const sourceBadge = (provider: OrgProvider) => {
    if (provider.credential_source === 'organization') {
      return (
        <span className="text-[10px] uppercase font-bold text-brand-teal bg-brand-teal/10 px-2 py-0.5 rounded-full">
          Org key
        </span>
      );
    }
    if (provider.credential_source === 'platform') {
      return (
        <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
          Platform default
        </span>
      );
    }
    return (
      <span className="text-[10px] uppercase font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
        Not configured
      </span>
    );
  };

  const field = (provider: OrgProvider, spec: ProviderCredentialSpecField) => {
    const stored = provider.credentials.fields[spec.key];
    const asRef = spec.secret && useRef[spec.key];
    return (
      <div key={spec.key} className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-navy-900">
            {spec.label}
            {spec.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          {spec.accepts_secret_ref && (
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
              <input
                type="checkbox"
                checked={!!useRef[spec.key]}
                onChange={(e) =>
                  setUseRef((r) => ({ ...r, [spec.key]: e.target.checked }))
                }
                className="rounded border-slate-300"
              />
              Secret Manager reference
            </label>
          )}
        </div>
        <input
          type={spec.secret && !asRef ? 'password' : 'text'}
          value={draft[spec.key] ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, [spec.key]: e.target.value }))}
          placeholder={
            asRef
              ? 'secret name, e.g. higgsfield-arthaus'
              : stored
                ? `saved (${stored.preview}) — leave blank to keep`
                : spec.label
          }
          className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
        />
        {spec.help && <p className="text-[11px] text-slate-500">{spec.help}</p>}
        {asRef && (
          <p className="text-[11px] text-slate-500">
            Stores the secret&apos;s <strong>name</strong>. The value itself never
            reaches this database.
          </p>
        )}
      </div>
    );
  };

  if (!orgUid) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">Creative Providers</h2>
        </div>
        <button
          onClick={() => void load()}
          className="text-slate-400 hover:text-slate-600"
          title="Reload"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="p-5 space-y-3">
        <p className="text-xs text-slate-500">
          External creative agents this organization can hand a brief to. An org
          key overrides the platform default; providers with neither are listed
          so you can see what is available to configure.
        </p>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-3 py-2">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading && providers.length === 0 && (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
            <Loader2 size={16} className="animate-spin" /> Loading providers…
          </div>
        )}

        {providers.map((provider) => {
          const open = editing === provider.provider_id;
          const result = tests[provider.provider_id];
          return (
            <div
              key={provider.provider_id}
              className="border border-slate-200 rounded-lg overflow-hidden"
            >
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        provider.status.available ? 'bg-emerald-500' : 'bg-slate-300'
                      }`}
                    />
                    <span className="font-medium text-sm text-navy-900">
                      {provider.display_name}
                    </span>
                    {sourceBadge(provider)}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {provider.capabilities.join(' · ')}
                  </p>
                  {!provider.status.available && provider.status.detail && !open && (
                    <p className="text-[11px] text-amber-600 mt-0.5">
                      {provider.provider_id === 'higgsfield'
                        ? 'Connect your organization’s Higgsfield API key to generate in NeuroGraph.'
                        : provider.status.detail}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {result && (
                    <span
                      className={`flex items-center gap-1 text-[11px] ${
                        result.ok ? 'text-emerald-600' : 'text-red-600'
                      }`}
                      title={result.detail}
                    >
                      {result.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                      {result.ok ? (result.verified === false ? 'configured; generation not tested' : 'reachable') : 'failed'}
                    </span>
                  )}
                  {provider.credentials.configured && (
                    <button
                      onClick={() => void test(provider.provider_id)}
                      disabled={testing === provider.provider_id}
                      className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                    >
                      {testing === provider.provider_id ? 'Testing…' : 'Test'}
                    </button>
                  )}
                  {provider.credential_spec.length > 0 && (
                    <button
                      onClick={() =>
                        open ? setEditing(null) : beginEdit(provider)
                      }
                      className="text-xs px-2 py-1 rounded-md bg-brand-teal text-white hover:opacity-90"
                    >
                      {open ? 'Cancel' : provider.credentials.configured ? 'Edit' : 'Configure'}
                    </button>
                  )}
                </div>
              </div>

              {open && (
                <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-3 bg-slate-50/50">
                  {provider.provider_id === 'higgsfield' && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-navy-900" htmlFor="higgsfield-complete-key">Complete Higgsfield API key</label>
                      <input
                        id="higgsfield-complete-key"
                        type="password"
                        autoComplete="new-password"
                        value={completeKey}
                        onChange={(event) => setCompleteKey(event.target.value)}
                        placeholder="Paste KEY_ID:KEY_SECRET from Higgsfield"
                        className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
                      />
                      <p className="text-[11px] text-slate-500">Paste the complete key copied from Higgsfield. NeuroGraph separates the two parts automatically. This saves the credential directly in your organization’s configuration.</p>
                    </div>
                  )}
                  {provider.provider_id === 'higgsfield' ? (
                    <details>
                      <summary className="text-xs text-slate-500 cursor-pointer">Advanced: separate fields or Secret Manager references</summary>
                      <div className="space-y-3 pt-3">{provider.credential_spec.map((spec) => field(provider, spec))}</div>
                    </details>
                  ) : (
                    provider.credential_spec.map((spec) => field(provider, spec))
                  )}
                  <div className="flex items-center justify-between pt-1">
                    {provider.credentials.configured ? (
                      <button
                        onClick={() => void clear(provider.provider_id)}
                        disabled={saving}
                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        <Trash2 size={13} /> Remove org key
                      </button>
                    ) : (
                      <span />
                    )}
                    <button
                      onClick={() => void save(provider)}
                      disabled={saving}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-navy-900 text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {saving ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Check size={13} />
                      )}
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
